import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Wrench, DoorOpen, AlignJustify, Maximize2, SquareStack, Umbrella, Hammer } from "lucide-react";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

const SERVICES = [
  { icon: DoorOpen, label: "Fenster", desc: "Holz- und Kunststofffenster in höchster Qualität, nach Ihren Maßen gefertigt.", delay: 0 },
  { icon: AlignJustify, label: "Rollläden", desc: "Elektrische und manuelle Rollläden – Wärme, Sicherheit und Schutz.", delay: 0.05 },
  { icon: DoorOpen, label: "Haustüren", desc: "Repräsentative Haustüren aus Holz oder Holz-Aluminium, individuell gestaltet.", delay: 0.1 },
  { icon: SquareStack, label: "Innenausbau & Türen", desc: "Zimmertüren, Zargen und maßgeschneiderter Innenausbau.", delay: 0.15 },
  { icon: Maximize2, label: "Möbel nach Maß", desc: "Einbauschränke, Küchen und Möbel – präzise auf Ihre Räume abgestimmt.", delay: 0.2 },
  { icon: Umbrella, label: "Überdachungen", desc: "Carports, Terrassenüberdachungen und Vordächer in Holzbauweise.", delay: 0.25 },
  { icon: Hammer, label: "Reparaturservice", desc: "Schnelle, zuverlässige Reparaturen an Fenstern, Türen und Möbeln.", delay: 0.3 },
  { icon: Wrench, label: "Treppenbau", desc: "Holztreppen als Maßanfertigung – funktional und gestalterisch anspruchsvoll.", delay: 0.35 },
];

// PLATZHALTER: durch echte Referenzfotos ersetzen
const GALLERY = [
  "https://images.pexels.com/photos/7166640/pexels-photo-7166640.jpeg?auto=compress&cs=tinysrgb&w=600",
  "https://images.pexels.com/photos/8584020/pexels-photo-8584020.jpeg?auto=compress&cs=tinysrgb&w=600",
  "https://images.unsplash.com/photo-1565538420870-da08ff96a207?q=80&w=600&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1600121848594-d8644e57abab?q=80&w=600&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=600&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1524758631624-e2822e304c36?q=80&w=600&auto=format&fit=crop",
];

const Schreinerei = () => (
  <main>
    {/* HERO */}
    <section className="relative h-[60vh] overflow-hidden" data-testid="schreinerei-hero">
      {/* PLATZHALTER: durch echtes Werkstatt-Foto (Lorenkamp 4) ersetzen */}
      <div
        className="absolute inset-0 bg-cover bg-center animate-ken-burns"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1504148455328-c376907d081c?q=80&w=1920&auto=format&fit=crop')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#8B5E3C]/70 to-[#2B2B2B]/40" />
      <div className="absolute inset-0 flex items-end">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full pb-16">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <p className="text-[#C9A66B] uppercase tracking-[0.35em] text-xs font-manrope mb-3">Handwerk</p>
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-3">Schreinerei Loos</h1>
            <p className="text-white/75 font-manrope text-base sm:text-lg max-w-lg">Qualitätshandwerk aus Haltern am See – seit über 80 Jahren.</p>
          </motion.div>
        </div>
      </div>
    </section>

    {/* INTRO */}
    <section className="py-20 bg-[#F8F5F0]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <motion.p {...fadeUp(0)} className="uppercase tracking-[0.3em] text-xs text-[#8B5E3C] font-manrope mb-3">Leistungen</motion.p>
        <motion.h2 {...fadeUp(0.1)} className="font-playfair text-3xl sm:text-4xl text-[#2B2B2B] mb-6">Was wir für Sie fertigen</motion.h2>
        <motion.p {...fadeUp(0.2)} className="text-[#2B2B2B]/60 font-manrope leading-relaxed">
          In unserer Werkstatt in Lorenkamp 4 entstehen hochwertige Holzprodukte – von der Planung bis zur Montage. Jedes Stück wird mit Sorgfalt und dem handwerklichen Anspruch der Familie Loos gefertigt.
        </motion.p>
      </div>
    </section>

    {/* SERVICES GRID */}
    <section className="pb-24 bg-[#F8F5F0]" data-testid="services-grid">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: s.delay }}
              className="bg-white border border-[#8B5E3C]/10 p-7 group hover:border-[#8B5E3C]/30 transition-all duration-300"
              data-testid={`service-card-${i}`}
            >
              <div className="w-10 h-10 rounded-sm bg-[#8B5E3C]/10 flex items-center justify-center mb-4 group-hover:bg-[#8B5E3C]/20 transition-colors">
                <s.icon size={20} className="text-[#8B5E3C]" />
              </div>
              <h3 className="font-playfair text-lg text-[#2B2B2B] mb-2">{s.label}</h3>
              <p className="text-[#2B2B2B]/55 text-sm font-manrope leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* GALLERY */}
    <section className="py-20 bg-[#2B2B2B]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.p {...fadeUp(0)} className="text-center uppercase tracking-[0.3em] text-xs text-[#C9A66B] font-manrope mb-3">Referenzen</motion.p>
        <motion.h2 {...fadeUp(0.1)} className="text-center font-playfair text-3xl text-white mb-12">Ausgewählte Projekte</motion.h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {GALLERY.map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="relative h-48 sm:h-56 overflow-hidden group"
              data-testid={`gallery-item-${i}`}
            >
              {/* PLATZHALTER: durch echtes Referenzfoto ersetzen */}
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                style={{ backgroundImage: `url(${img})` }}
              />
              <div className="absolute inset-0 bg-[#2B2B2B]/20 group-hover:bg-[#2B2B2B]/0 transition-colors duration-300" />
              <div className="absolute bottom-2 left-2 bg-black/50 text-white/50 text-[9px] font-manrope px-1.5 py-0.5">
                PLATZHALTER – Referenzfoto {i + 1}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* CONTACT CTA */}
    <section className="py-20 bg-[#F8F5F0]" data-testid="schreinerei-contact">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <motion.div {...fadeUp(0)}>
            <p className="uppercase tracking-[0.3em] text-xs text-[#8B5E3C] font-manrope mb-3">Werkstatt</p>
            <h2 className="font-playfair text-3xl sm:text-4xl text-[#2B2B2B] mb-6">Besuchen Sie uns</h2>
            <div className="space-y-4 font-manrope text-sm text-[#2B2B2B]/65">
              <div className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 shrink-0 text-[#8B5E3C]" />
                <div>
                  <div className="font-semibold text-[#2B2B2B]">Schreinerei Loos</div>
                  <div>Lorenkamp 4, 45721 Haltern am See</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={16} className="shrink-0 text-[#8B5E3C]" />
                <a href="tel:023645042567" className="hover:text-[#8B5E3C] transition-colors font-semibold text-[#2B2B2B]">
                  02364 / 5042567
                </a>
              </div>
            </div>
          </motion.div>
          <motion.div {...fadeUp(0.15)} className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/kontakt"
              data-testid="schreinerei-kontakt-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#8B5E3C] text-white px-6 py-3 text-sm font-manrope font-semibold hover:bg-[#7a5235] transition-colors"
            >
              Anfrage stellen <ArrowRight size={14} />
            </Link>
            <a
              href="tel:023645042567"
              className="inline-flex items-center justify-center gap-2 border border-[#8B5E3C] text-[#8B5E3C] px-6 py-3 text-sm font-manrope hover:bg-[#8B5E3C] hover:text-white transition-all"
            >
              <Phone size={14} /> Anrufen
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  </main>
);

export default Schreinerei;
